/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback } from "react";
import { ReadmeEditorView } from "../ReadmeEditorView";

export interface IReadmeLinkProps {
	href?: string;
	children?: React.ReactNode;
	view: ReadmeEditorView;
}

/**
 * Checks if a URL is an external/absolute link
 */
function isExternalLink(href: string): boolean {
	// Absolute URLs (http://, https://, //, etc.)
	if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//")) {
		return true;
	}

	// Mailto, tel, etc.
	if (/^(mailto|tel):/i.test(href)) {
		return true;
	}

	return false;
}

/**
 * Checks if a URL is an anchor-only link
 */
function isAnchorLink(href: string): boolean {
	return href.startsWith("#");
}

/**
 * Scrolls to an element by its ID
 */
function scrollToAnchor(anchor: string): void {
	const id = anchor.substring(1); // Remove the # prefix
	const element = document.getElementById(id);

	if (element) {
		element.scrollIntoView({ behavior: "smooth", block: "start" });
	}
}

/**
 * Normalizes a relative path to a blueprint path
 * Handles paths like "views/Main.yaml", "./views/Main", "../actions/Test.yaml"
 * Returns normalized path (e.g., "views/Main")
 */
function normalizeBlueprintPath(href: string): string {
	// Remove leading ./
	let path = href.replace(/^\.\//, "");

	// Remove .yaml or .yml extension
	path = path.replace(/\.ya?ml$/i, "");

	return path;
}

export const ReadmeLink: FC<IReadmeLinkProps> = ({ href, children, view }) => {
	const isRelative = href && !isExternalLink(href) && !isAnchorLink(href);

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			if (!href) {
				return;
			}

			// Handle anchor links - scroll to element
			if (isAnchorLink(href)) {
				e.preventDefault();
				scrollToAnchor(href);
				return;
			}

			// Handle external links - open in new window
			if (isExternalLink(href)) {
				e.preventDefault();
				window.open(href, "_blank", "noopener,noreferrer");
				return;
			}

			// Handle relative/internal links - try to open blueprint
			e.preventDefault();
			const blueprintPath = normalizeBlueprintPath(href);
			view.openBlueprintByPath(blueprintPath);
		},
		[ href, view ]
	);

	// For relative links, show # to indicate local navigation
	const displayHref = isRelative ? "#blueprint:" + href : href;

	return (
		<a href={displayHref} onClick={handleClick}>
			{children}
		</a>
	);
};
